"use client";

import { ReactNode, useEffect, useState } from "react";
import { onAuthStateChanged, User, IdTokenResult, signInWithEmailAndPassword, signOut as firebaseSignOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { deleteServerCookie, getServerCookie, setServerCookie } from "@postmedia-ai/next-pro";
import { useRouter } from "next/navigation";
import { AuthContext } from "../contexts/AuthContext";
import { auth } from "../clientApp";
import { COOKIE_KEYS } from "../constants/cookies";
import { CustomClaims, UserRole } from "../types/auth";
import Loading from "@/app/loading";

// 인증 상태를 관리하고 하위 컴포넌트에 제공하는 Provider
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [idTokenResult, setIdTokenResult] = useState<IdTokenResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Firebase 인증 상태 변화 감지
  useEffect(() => {
    let initialLoaded = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('onAuthStateChanged user:', user);
      }
      setLoading(true);
      if (user) {
        const tokenResult = await user.getIdTokenResult();
        const customClaims: CustomClaims = {
            role: (tokenResult.claims.role as UserRole) || UserRole.USER,
        };

        // 쿠키의 토큰과 비교하여 다를 경우에만 업데이트
        const cookieToken = await getServerCookie(COOKIE_KEYS.AUTH_TOKEN);
        if (cookieToken !== tokenResult.token) {
          console.log('Token changed, syncing cookie...');
          await setServerCookie(COOKIE_KEYS.AUTH_TOKEN, tokenResult.token);
        }

        setRole(customClaims.role);
        setIdTokenResult(tokenResult);
        setUser(user);
      } else {
        // 로그아웃 시 상태 초기화
        if (initialLoaded) {
          console.log('User logged out');
          
          setRole(null);
          setIdTokenResult(null);
          setUser(null);
          await deleteServerCookie(COOKIE_KEYS.AUTH_TOKEN);

          // 페이지 리로드
          router.refresh();
        }
      }
      setLoading(false);
      setMounted(true);
      initialLoaded = true;
    });

    return () => unsubscribe();
  }, []);

  // 토큰 갱신 주기적 실행 (55분마다)
  useEffect(() => {
    if (!user) return;
    
    const refreshInterval = setInterval(async () => {
      console.log('Refreshing ID token...');
      const newToken = await user.getIdToken(true); // 강제 갱신
      console.log('New ID token:', newToken?.substring(0, 20) + '...');
      await setServerCookie(COOKIE_KEYS.AUTH_TOKEN, newToken);
    }, 55 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [user]);

  // 구독 정보 초기화 (없으면 Standard 플랜 자동 생성)
  const initializeSubscription = async (idToken: string): Promise<void> => {
    try {
      const response = await fetch('/api/subscription/status', {
        headers: {
          'Cookie': `${COOKIE_KEYS.AUTH_TOKEN}=${idToken}`,
        },
      });
      if (response.ok) {
        console.log('Subscription initialized');
      }
    } catch (error) {
      console.error('Error initializing subscription:', error);
    }
  };

  // 로그인 함수
  const signIn = async (email: string, password: string): Promise<User> => {
    console.log('signIn called with:', { email });
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();
      console.log('signIn userCredential:', userCredential);
      await setServerCookie(COOKIE_KEYS.AUTH_TOKEN, idToken);

      // 구독 정보 초기화 (없으면 Firestore에 Standard 플랜 생성)
      await initializeSubscription(idToken);

      setLoading(false);
      return userCredential.user;
    } catch (error) {
      console.error('Login failed', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 구글 로그인 함수
  const signInWithGoogle = async (): Promise<User> => {
    console.log('signInWithGoogle called');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const idToken = await userCredential.user.getIdToken();
      console.log('signInWithGoogle userCredential:', userCredential);
      await setServerCookie(COOKIE_KEYS.AUTH_TOKEN, idToken);

      // 구독 정보 초기화 (없으면 Firestore에 Standard 플랜 생성)
      await initializeSubscription(idToken);

      setLoading(false);
      return userCredential.user;
    } catch (error) {
      console.error('Google login failed', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // 로그아웃 함수
  const signOut = async () => {
    console.log('signOut');
    setLoading(true);
    await firebaseSignOut(auth);
    setLoading(false);
  };

  // console.log('AuthProvider render:', { user, idTokenResult, loading, role });

  // 로딩 중일 때 전체화면 로딩 페이지 표시
  if (!mounted) {
    return <Loading />;
  }

  return (
    <AuthContext.Provider value={{ user, idTokenResult, loading, role, signIn, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};